import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import {
  Layers, Plus, Trash2, Loader2, Sparkles, Radio, KeyRound, LogOut, Copy, Printer,
  Power, CheckCircle2, QrCode, Download,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

const printCard = (c) => {
  const w = window.open('', '_blank', 'width=600,height=700');
  if (!w) return;
  w.document.write(`<html><head><title>Zone Setup — ${c.zone}</title>
    <style>body{font-family:system-ui,sans-serif;padding:40px;color:#111}
    h1{font-size:20px;margin:0 0 4px}.sub{color:#666;margin:0 0 24px}
    .row{margin:14px 0}.lbl{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#888}
    .val{font-size:18px;font-weight:700;font-family:monospace}</style></head>
    <body><h1>${c.property}</h1><p class="sub">Zone player setup — ${c.zone}</p>
    <div class="row"><div class="lbl">Login URL</div><div class="val">${c.login_url}</div></div>
    <div class="row"><div class="lbl">Email</div><div class="val">${c.email}</div></div>
    <div class="row"><div class="lbl">Password</div><div class="val">${c.password}</div></div>
    <p style="margin-top:32px;color:#888;font-size:12px">Sign in on the zone's screen. One device per zone — a new sign-in signs the previous one out.</p>
    </body></html>`);
  w.document.close(); w.focus(); w.print();
};

const downloadZoneQr = (zone) => {
  const url = `${FRONTEND_URL}/v/${zone.venue_code}`;
  const canvas = document.querySelector(`#zone-qr-canvas-${zone.id} canvas`);
  if (!canvas) { toast.error('QR not ready yet, try again'); return; }
  const a = document.createElement('a');
  a.href = canvas.toDataURL('image/png');
  a.download = `zone-qr-${(zone.name || 'zone').toLowerCase().replace(/\s+/g, '-')}.png`;
  a.click();
  toast.success('QR downloaded');
};

const ZoneQrCard = ({ zone }) => {
  if (!zone.venue_code) return null;
  const url = `${FRONTEND_URL}/v/${zone.venue_code}`;
  return (
    <div className="mt-2 flex items-center gap-3 rounded-lg bg-black/40 border border-white/5 p-2" data-testid={`zone-qr-${zone.id}`}>
      <div className="bg-white p-1 rounded">
        <QRCodeSVG value={url} size={56} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1"><QrCode className="w-3 h-3" /> Guest requests QR</p>
        <p className="text-[11px] text-gray-300 font-mono truncate">{url}</p>
      </div>
      {/* hidden high-res canvas for PNG export */}
      <div id={`zone-qr-canvas-${zone.id}`} style={{ position: 'absolute', left: -99999, top: 0 }}>
        <QRCodeCanvas value={url} size={512} />
      </div>
      <Button onClick={() => downloadZoneQr(zone)} className="h-7 px-2.5 shrink-0 rounded-full text-[11px] bg-white/5 hover:bg-white/15 text-gray-200 whitespace-nowrap" data-testid={`zone-qr-download-${zone.id}`}>
        <Download className="w-3 h-3 mr-1" /> Download
      </Button>
    </div>
  );
};

const CredentialsCard = ({ creds, onClose }) => (
  <div className="rounded-2xl border border-[#39FF14]/40 bg-[#39FF14]/5 p-4 space-y-3" data-testid="zone-credentials-card">
    <div className="flex items-center gap-2 text-[#39FF14] font-bold text-sm"><CheckCircle2 className="w-4 h-4" /> Credentials — shown once</div>
    <div className="text-xs space-y-1 font-mono bg-black/40 rounded-lg p-3">
      <div><span className="text-gray-500">Property:</span> {creds.property}</div>
      <div><span className="text-gray-500">Zone:</span> {creds.zone}</div>
      <div><span className="text-gray-500">URL:</span> {creds.login_url}</div>
      <div><span className="text-gray-500">Email:</span> {creds.email}</div>
      <div data-testid="zone-cred-password"><span className="text-gray-500">Password:</span> <span className="text-white font-bold">{creds.password}</span></div>
    </div>
    <div className="flex gap-2">
      <Button onClick={() => { navigator.clipboard.writeText(`URL: ${creds.login_url}\nEmail: ${creds.email}\nPassword: ${creds.password}`); toast.success('Credentials copied'); }}
        className="h-8 rounded-full text-xs bg-white/10 hover:bg-white/20 text-white" data-testid="copy-credentials-btn"><Copy className="w-3.5 h-3.5 mr-1" /> Copy credentials</Button>
      <Button onClick={() => printCard(creds)} className="h-8 rounded-full text-xs bg-white/10 hover:bg-white/20 text-white" data-testid="print-card-btn"><Printer className="w-3.5 h-3.5 mr-1" /> Print setup card</Button>
      <Button onClick={onClose} className="h-8 rounded-full text-xs bg-[#39FF14] text-black ml-auto">Done</Button>
    </div>
  </div>
);

export const ZonesManagerDialog = ({ client, open, onOpenChange, onChanged }) => {
  const [loading, setLoading] = useState(true);
  const [multiZone, setMultiZone] = useState(false);
  const [zones, setZones] = useState([]);
  const [busy, setBusy] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [form, setForm] = useState({ name: '', mode: 'ai', requests_enabled: true, default_genre: '', default_energy: 'chill', login_email: '', base_playlist_id: '' });
  const [creds, setCreds] = useState(null);

  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    try {
      const [zr, pr] = await Promise.all([
        axios.get(`${API}/admin/clients/${client.id}/zones`),
        axios.get(`${API}/admin/clients/${client.id}/playlists`).catch(() => ({ data: { playlists: [] } })),
      ]);
      setMultiZone(zr.data.multi_zone);
      setZones(zr.data.zones || []);
      setPlaylists(pr.data.playlists || []);
      setForm((f) => ({ ...f, mode: client.client_type === 'commercial' ? 'commercial' : 'ai' }));
    } catch { toast.error('Failed to load zones'); }
    finally { setLoading(false); }
  }, [client]);
  useEffect(() => { if (open) { setCreds(null); load(); } }, [open, load]);

  const call = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); if (okMsg) toast.success(okMsg); await load(); onChanged && onChanged(); return r; }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); throw e; }
    finally { setBusy(false); }
  };

  const toggleMulti = (enabled) => call(() => axios.put(`${API}/admin/clients/${client.id}/multi-zone`, { enabled }), enabled ? 'Multi-zone on' : 'Multi-zone off').then(() => setMultiZone(enabled));
  const addZone = async () => {
    if (!form.name.trim() || !form.login_email.trim()) { toast.error('Zone name and login email are required'); return; }
    const r = await call(() => axios.post(`${API}/admin/clients/${client.id}/zones`, form), 'Zone created');
    setCreds(r.data.credentials);
    setForm((f) => ({ ...f, name: '', login_email: '', base_playlist_id: '' }));
  };
  const resetPw = async (z) => { const r = await call(() => axios.post(`${API}/admin/zones/${z.id}/reset-password`), 'Password reset'); setCreds(r.data.credentials); };
  const signOutAll = (z) => call(() => axios.post(`${API}/admin/zones/${z.id}/signout-all`), 'Signed out all devices');
  const toggleActive = (z) => call(() => axios.put(`${API}/admin/zones/${z.id}`, { active: !z.active }), z.active ? 'Zone deactivated' : 'Zone activated');
  const setBase = (z, pid) => call(() => axios.put(`${API}/admin/zones/${z.id}`, { base_playlist_id: pid || null }), 'Base playlist updated');
  const removeZone = async (z) => {
    try {
      await axios.delete(`${API}/admin/zones/${z.id}`);
      toast.success('Zone removed'); await load(); onChanged && onChanged();
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm('This zone has play history. Delete anyway?')) {
          await axios.delete(`${API}/admin/zones/${z.id}?confirm=true`);
          toast.success('Zone removed'); await load(); onChanged && onChanged();
        }
      } else toast.error(e?.response?.data?.detail || 'Could not remove');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-3xl w-[94vw] max-h-[88vh] overflow-y-auto overflow-x-hidden" data-testid="zones-manager-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-[#00FFFF]" /> Zones · {client?.name}</DialogTitle></DialogHeader>
        {loading ? <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 text-gray-500 animate-spin" /></div> : (
          <div className="space-y-4 py-1">
            <div className="flex items-center gap-3 p-3 rounded-2xl bg-black/30 border border-white/5">
              <Switch checked={multiZone} onCheckedChange={toggleMulti} disabled={busy} data-testid="zones-multi-toggle" />
              <div className="text-xs"><p className="text-white font-semibold">{multiZone ? 'Multi-zone ON' : 'Multi-zone OFF'}</p>
                <p className="text-gray-500">{multiZone ? 'Zones show on the owner dashboard and accept zone-device logins.' : 'Single-venue mode (hidden Main zone only).'}</p></div>
            </div>

            {creds && <CredentialsCard creds={creds} onClose={() => setCreds(null)} />}

            <div className="space-y-2" data-testid="zones-list">
              {zones.map((z) => (
                <div key={z.id} className={`p-3 rounded-xl border ${z.active ? 'bg-black/30 border-white/5' : 'bg-black/20 border-white/5 opacity-60'}`} data-testid={`zone-item-${z.id}`}>
                  <div className="flex items-center gap-2">
                    {z.mode === 'ai' ? <Sparkles className="w-4 h-4 text-[#00FFFF]" /> : <Radio className="w-4 h-4 text-[#FF007F]" />}
                    <span className="text-sm text-white font-semibold">{z.name}</span>
                    {z.is_main && <span className="text-[9px] uppercase text-gray-500 bg-white/5 px-1.5 py-0.5 rounded">Main</span>}
                    {!z.active && <span className="text-[9px] uppercase text-[#FFB300]">inactive</span>}
                    <span className={`ml-auto flex items-center gap-1 text-[10px] ${z.online ? 'text-[#39FF14]' : 'text-gray-500'}`}><span className={`w-1.5 h-1.5 rounded-full ${z.online ? 'bg-[#39FF14]' : 'bg-gray-600'}`} />{z.online ? 'LIVE' : 'offline'}</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1 flex flex-wrap gap-x-3">
                    <span>{z.base_summary}</span>
                    <span>requests {z.requests_enabled ? 'on' : 'off'}</span>
                    {z.device_email && <span>· {z.device_email}</span>}
                    {z.last_seen && <span>· last seen {new Date(z.last_seen).toLocaleString()}</span>}
                  </div>
                  {!z.is_main && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <Button onClick={() => resetPw(z)} disabled={busy} className="h-7 px-2.5 rounded-full text-[11px] bg-white/5 hover:bg-white/15 text-gray-200" data-testid={`zone-reset-${z.id}`}><KeyRound className="w-3 h-3 mr-1" /> Reset password</Button>
                      <Button onClick={() => signOutAll(z)} disabled={busy} className="h-7 px-2.5 rounded-full text-[11px] bg-white/5 hover:bg-white/15 text-gray-200" data-testid={`zone-signout-${z.id}`}><LogOut className="w-3 h-3 mr-1" /> Sign out all</Button>
                      <Button onClick={() => toggleActive(z)} disabled={busy} className="h-7 px-2.5 rounded-full text-[11px] bg-white/5 hover:bg-white/15 text-gray-200" data-testid={`zone-toggle-active-${z.id}`}><Power className="w-3 h-3 mr-1" /> {z.active ? 'Deactivate' : 'Activate'}</Button>
                      <Button onClick={() => removeZone(z)} disabled={busy} className="h-7 px-2.5 rounded-full text-[11px] bg-[#FF3B3B]/10 hover:bg-[#FF3B3B]/20 text-[#FF6B6B]" data-testid={`zone-delete-${z.id}`}><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
                    </div>
                  )}
                  {!z.is_main && z.mode === 'commercial' && (
                    <div className="mt-2 flex items-center gap-2" data-testid={`zone-base-picker-${z.id}`}>
                      <span className="text-[10px] uppercase tracking-wider text-gray-500">Base playlist</span>
                      <select
                        value={z.base_playlist_id || ''}
                        onChange={(e) => setBase(z, e.target.value)}
                        disabled={busy}
                        style={{ colorScheme: 'dark' }}
                        className="bg-black/40 border border-white/10 rounded-lg h-7 px-2 text-xs text-white flex-1"
                        data-testid={`zone-base-select-${z.id}`}
                      >
                        <option value="">— none —</option>
                        {playlists.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.track_count})</option>)}
                      </select>
                    </div>
                  )}
                  {!z.is_main && z.requests_enabled && <ZoneQrCard zone={z} />}
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-white/10 p-3 space-y-2" data-testid="zone-add-form">
              <p className="text-xs font-bold text-gray-300 uppercase tracking-wider">Add a zone</p>
              <div className="flex gap-2">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Zone name (e.g. Patio)" className="bg-black/40 border-white/10 h-9 text-sm flex-1" data-testid="zone-new-name" />
                <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} style={{ colorScheme: 'dark' }} className="bg-black/40 border border-white/10 rounded-lg h-9 px-2 text-xs text-white" data-testid="zone-new-mode">
                  <option value="ai">AI</option><option value="commercial">Commercial</option>
                </select>
              </div>
              <Input value={form.login_email} onChange={(e) => setForm({ ...form, login_email: e.target.value })} placeholder="Device login email" className="bg-black/40 border-white/10 h-9 text-sm" data-testid="zone-new-email" />
              {form.mode === 'ai' && (
                <div className="flex gap-2">
                  <Input value={form.default_genre} onChange={(e) => setForm({ ...form, default_genre: e.target.value })} placeholder="Default genre (e.g. deephouse)" className="bg-black/40 border-white/10 h-9 text-sm flex-1" data-testid="zone-new-genre" />
                  <select value={form.default_energy} onChange={(e) => setForm({ ...form, default_energy: e.target.value })} style={{ colorScheme: 'dark' }} className="bg-black/40 border border-white/10 rounded-lg h-9 px-2 text-xs text-white" data-testid="zone-new-energy">
                    {['chill', 'upbeat', 'groovy', 'dance'].map((e) => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
              )}
              {form.mode === 'commercial' && (
                <div className="space-y-1">
                  <label className="text-[10px] uppercase tracking-wider text-gray-500">Base playlist (plays when nothing else is scheduled)</label>
                  <select value={form.base_playlist_id} onChange={(e) => setForm({ ...form, base_playlist_id: e.target.value })} style={{ colorScheme: 'dark' }} className="bg-black/40 border border-white/10 rounded-lg h-9 px-2 text-xs text-white w-full" data-testid="zone-new-base-playlist">
                    <option value="">— none —</option>
                    {playlists.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.track_count})</option>)}
                  </select>
                  {playlists.length === 0 && <p className="text-[10px] text-[#FFB300]">No playlists assigned to this venue yet. Assign one in Playlists first.</p>}
                </div>
              )}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs text-gray-300"><Switch checked={form.requests_enabled} onCheckedChange={(v) => setForm({ ...form, requests_enabled: v })} data-testid="zone-new-requests" /> Guest requests on</label>
                <Button onClick={addZone} disabled={busy} className="bg-[#39FF14] text-black hover:bg-[#2fd60f] rounded-full font-bold h-9 px-4" data-testid="zone-add-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create + login</>}</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ZonesManagerDialog;
